import { withStyles } from '@material-ui/core/styles';
import _ from 'lodash';
import React, { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import NavigationBar from '../NavigationViews/NavigationBar';
import translations from '../../translations/i18next';
import { COMPONENT_BACKGROUND } from '../../styles/styleConsts';
import { isMobileBrowser } from '../../utils/utilsFunctions';
import ChatIcon from '../../components/Icons/ChatIcon';
import MDTextInputView from '../../components/Forms/FormsComponents/MDTextInput/MDTextInputView';
import { Fab, IconButton, InputAdornment } from '@material-ui/core';
import { ArrowDownward, Send } from '@material-ui/icons';
import { useChat } from '../../hooks/ai/useChat';
import ChatMessages from '../../components/Chat/ChatMessages';
import { useLocation, useHistory } from 'react-router-dom';

const styles = () => {
  const isMobile = isMobileBrowser();
  return {
    container: {
      padding: 20,
      backgroundColor: COMPONENT_BACKGROUND,
      height: isMobile ? '137vh' : '78vh',
      flexDirection: 'column'
    },
    list: {
      height: 'calc(100% - 20px)',
      width: '100%',
      overflowY: 'scroll'
    },
    sendButton: {
      flex: 1,
      display: 'flex',
      alignItems: 'flex-end',
      justifyContent: 'flex-end'
    },
    newTextButton: {
      position: 'absolute',
      bottom: 50,
      right: '50%',
      zIndex: 2
    }
  };
};

const ChatAIView = (props) => {
  const { classes } = props;
  const selectedLanguage = useSelector((s) => s.utils.selectedLanguage);
  const { sendQuestion, retryQuestion, messages } = useChat();
  const [question, setQuestion] = useState('');
  const listRef = useRef(null);
  const sentinelRef = useRef(null);
  const [isAtBottom, setIsAtBottom] = useState(true);
  const location = useLocation();
  const { state } = location || {};
  const history = useHistory();

  useEffect(() => {
    if (state && state.quiz) {
      sendQuestion(translations.t('chat.coachAIPrompt', { id: state.quiz.id, language: selectedLanguage }), {
        hide: true
      });
      history.replace({ ...history.location, state: null });
    }

    const io = addObserver();
    return () => (io ? io.disconnect() : undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const addObserver = () => {
    const root = listRef.current;
    const target = sentinelRef.current;
    if (!root || !target) return undefined;
    const io = new IntersectionObserver(
      (entries) => {
        const isBottom = entries[0]?.isIntersecting ?? false;
        setIsAtBottom(isBottom);
      },
      {
        root,
        threshold: 1.0,
        rootMargin: '0px 0px 15px 0px'
      }
    );

    io.observe(target);
    return io;
  };

  const scrollToBottom = () => {
    if (listRef && listRef.current)
      listRef.current.scrollTo({
        top: listRef.current.scrollHeight,
        behavior: 'smooth'
      });
  };

  const sendQuestionDisabled = React.useMemo(
    () => !question.trim() || messages.some((m) => m.type === 'Answer' && m.status === 'pending'),
    [messages, question]
  );

  const sendChatQuestion = () => {
    sendQuestion(question.trim());
    setQuestion('');
    requestAnimationFrame(() => {
      const el = listRef.current;
      if (!el) return;
      el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
    });
  };

  const handleKeyPress = (e) => {
    if (sendQuestionDisabled || !question.trim()) return;
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendChatQuestion();
    }
  };

  return (
    <div>
      <NavigationBar title={translations.t('header.coachAI')} icon={<ChatIcon color="white" />} />
      <div className={classes.container} style={{ position: 'relative' }}>
        <div className={classes.list} ref={listRef} style={{ position: 'relative' }}>
          <ChatMessages messages={messages} onRetryQuestion={retryQuestion} />
          <div ref={sentinelRef} style={{ height: 1 }} />
        </div>
        {!isAtBottom && (
          <Fab
            onClick={scrollToBottom}
            size="small"
            color="primary"
            aria-label="scroll to bottom"
            className={classes.newTextButton}
          >
            <ArrowDownward />
          </Fab>
        )}
        <div className={classes.sendButton}>
          <MDTextInputView
            multiline
            onChange={setQuestion}
            onKeyPress={handleKeyPress}
            value={question}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton disabled={sendQuestionDisabled} color="primary" onClick={sendChatQuestion}>
                    <Send />
                  </IconButton>
                </InputAdornment>
              )
            }}
          />
        </div>
      </div>
    </div>
  );
};

export default _.flow([withStyles(styles)])(ChatAIView);
